'use client';

import * as React from 'react';

import type { DayStripChip } from '@/lib/discover/dayStrip';

import { DAY_SECTION_SELECTOR } from './useLoadedDayDates';

/** A section counts as "under the bar" once its top is within this of the bar's bottom. */
export const SPY_CROSS_SLACK_PX = 8;
/** A far chip lands the list tail this far below the bar. */
export const TAIL_JUMP_GAP_PX = 16;
/** Fallback for browsers without `scrollend`: how long a jump holds the tapped chip. */
const JUMP_LOCK_FALLBACK_MS = 900;

const BAR_SELECTOR = '[data-testid="discover-results-bar"]';
/** Where a far day's rows will load: the tail, else the load-more control. */
const LIST_END_SELECTORS = [
  '[data-testid="discover-list-tail"]',
  '[data-testid="discover-list-view-load-more"]',
] as const;
const LIST_SELECTOR = '[data-testid="discover-list-view"]';

/**
 * The viewport line a day head should sit on: the bar's bottom while the bar
 * sticks under the nav (`top: var(--nav-stick-top)`), or the nav's bottom
 * when the bar is not a top band (the ≤720 floor bar). Read from the bar's
 * computed position and real height, never a constant.
 */
export function resolveAnchorLine(bar: HTMLElement | null): number {
  if (!bar) return 0;
  const style = getComputedStyle(bar);
  if (style.position === 'sticky') {
    return (Number.parseFloat(style.top) || 0) + bar.getBoundingClientRect().height;
  }
  // Not a top band: day heads land flush under the nav. Resolve the token by
  // measuring a probe, since its declared value may be a `var()`/`calc()`.
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;visibility:hidden;pointer-events:none;height:var(--nav-stick-top,0px)';
  (bar.parentElement ?? document.body).appendChild(probe);
  const stickTop = probe.getBoundingClientRect().height;
  probe.remove();
  return stickTop;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * The viewport-relative top a far chip lands under the bar: the list tail
 * (or the load-more control when the tail is not shown), else a point that
 * brings the list's last rows into view.
 */
function listEndTop(): number | null {
  for (const selector of LIST_END_SELECTORS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (el) return el.getBoundingClientRect().top;
  }
  const list = document.querySelector<HTMLElement>(LIST_SELECTOR);
  return list ? list.getBoundingClientRect().bottom - window.innerHeight / 2 : null;
}

function sectionFor(localDate: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`section[data-local-date="${localDate}"]`);
}

/**
 * Map a section's date onto the chip that represents it: itself, or the
 * leading on-now chip for any section dated before the first in-range chip
 * (DQ-5: in-progress events from earlier days).
 */
export function chipDateForSection(
  sectionDate: string,
  chips: readonly DayStripChip[],
): string | undefined {
  const onNow = chips.find((chip) => chip.state === 'on-now');
  const firstInRange = chips.find((chip) => chip.state !== 'on-now');
  if (onNow && firstInRange && sectionDate < firstInRange.localDate) return onNow.localDate;
  return chips.some((chip) => chip.localDate === sectionDate && chip.state !== 'on-now')
    ? sectionDate
    : undefined;
}

export interface UseDayStripSpyResult {
  /** The chip whose day is under the bar — `aria-current="date"`. */
  readonly activeDate: string | undefined;
  /** Jump to a chip's day (loaded) or to the list tail (far). */
  readonly selectChip: (chip: DayStripChip) => void;
}

/**
 * WEB-1080 — the day strip's scroll-spy, jump-to-day and active-chip centering.
 *
 * - **Spy:** a passive `scroll` listener throttled to one animation frame,
 *   re-run on `resize` and whenever the chips change. The active day is the
 *   last loaded section whose top has crossed the bar's bottom + 8px; before
 *   any has, the first chip (so the On now chip at scroll top, DQ-5). Only
 *   runs while day sections exist (the results state).
 * - **Jump:** a loaded chip scrolls the window so its section head sits flush
 *   under the bar; a far chip lands the list tail (else load-more, else the
 *   list's end) 16px under it; an empty
 *   chip never reaches here. The tapped chip is active at once and holds
 *   through the smooth scroll (until `scrollend`), so the spy cannot flicker
 *   through the days in between. `behavior: 'auto'` under reduced motion.
 * - **Centering:** when the active chip changes and the strip overflows, the
 *   strip — and only the strip — scrolls to centre it. Never
 *   `scrollIntoView`, which also scrolls ancestors and fights the sticky bar.
 */
export function useDayStripSpy(
  stripRef: React.RefObject<HTMLElement | null>,
  chips: readonly DayStripChip[],
  enabled: boolean,
): UseDayStripSpyResult {
  const [activeDate, setActiveDate] = React.useState<string | undefined>(undefined);
  const lockRef = React.useRef<string | null>(null);
  /** Frees the jump that currently holds `lockRef`; `null` when none does. */
  const releaseRef = React.useRef<(() => void) | null>(null);
  const chipsRef = React.useRef(chips);
  chipsRef.current = chips;

  const computeActive = React.useCallback((): string | undefined => {
    const current = chipsRef.current;
    if (current.length === 0) return undefined;
    const bar = stripRef.current?.closest<HTMLElement>(BAR_SELECTOR) ?? null;
    const line = resolveAnchorLine(bar) + SPY_CROSS_SLACK_PX;
    let crossed: string | undefined;
    document.querySelectorAll<HTMLElement>(DAY_SECTION_SELECTOR).forEach((section) => {
      const date = section.dataset.localDate;
      if (date && section.getBoundingClientRect().top <= line) crossed = date;
    });
    const mapped = crossed ? chipDateForSection(crossed, current) : undefined;
    return mapped ?? current[0]!.localDate;
  }, [stripRef]);

  // Spy.
  React.useEffect(() => {
    if (!enabled) {
      // A jump in flight would leave its lock held, so the next enable's
      // `run()` would bail and no chip would be current until a scroll.
      releaseRef.current?.();
      setActiveDate(undefined);
      return;
    }
    let frame = 0;
    const run = (): void => {
      frame = 0;
      if (lockRef.current) return;
      setActiveDate(computeActive());
    };
    const schedule = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(run);
    };
    run();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [enabled, chips, computeActive]);

  // Centering.
  React.useEffect(() => {
    const strip = stripRef.current;
    if (!strip || !activeDate || strip.scrollWidth <= strip.clientWidth) return;
    const chip = strip.querySelector<HTMLElement>(`[data-local-date="${activeDate}"]`);
    if (!chip) return;
    const stripBox = strip.getBoundingClientRect();
    const chipBox = chip.getBoundingClientRect();
    const left =
      strip.scrollLeft + (chipBox.left - stripBox.left) - (strip.clientWidth - chipBox.width) / 2;
    strip.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [activeDate, stripRef]);

  const selectChip = React.useCallback(
    (chip: DayStripChip) => {
      if (chip.state === 'empty') return;
      const bar = stripRef.current?.closest<HTMLElement>(BAR_SELECTOR) ?? null;
      const line = resolveAnchorLine(bar);
      const targetTop =
        chip.state === 'far'
          ? listEndTop()
          : (sectionFor(chip.localDate)?.getBoundingClientRect().top ?? null);
      if (targetTop === null) return;
      const gap = chip.state === 'far' ? TAIL_JUMP_GAP_PX : 0;
      const top = targetTop + window.scrollY - line - gap;
      const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';

      // Retire the previous jump first: its timer, frame and `scrollend`
      // listener must not free this jump's lock part-way through.
      releaseRef.current?.();
      lockRef.current = chip.localDate;
      setActiveDate(chip.localDate);
      let released = false;
      let timer = 0;
      let frame = 0;
      const release = (): void => {
        if (released) return;
        released = true;
        window.clearTimeout(timer);
        window.cancelAnimationFrame(frame);
        window.removeEventListener('scrollend', release);
        // Only the jump that holds the lock may free it.
        if (releaseRef.current === release) {
          releaseRef.current = null;
          lockRef.current = null;
        }
      };
      releaseRef.current = release;
      if (behavior === 'auto') {
        window.scrollTo({ top, behavior });
        // An instant jump settles this frame; free the spy after it.
        frame = window.requestAnimationFrame(release);
      } else {
        window.addEventListener('scrollend', release, { once: true });
        timer = window.setTimeout(release, JUMP_LOCK_FALLBACK_MS);
        window.scrollTo({ top, behavior });
      }
    },
    [stripRef],
  );

  // An unmounted strip leaves no lock, timer, frame or listener behind.
  React.useEffect(() => () => releaseRef.current?.(), []);

  return { activeDate, selectChip };
}
